import React, { useId } from 'react';
import PropTypes from 'prop-types';

/**
 * ابرار سطحة الخبر - Professional SVG Logo
 * Brand: Blue (#0E4A86) + Orange (#F26B38)
 */
export default function Logo({ size = 60, variant = 'full', className = '', colorScheme = 'default' }) {
  const svgId = useId().replace(/:/g, '');
  const logoGradId = `logoGrad-${svgId}`;
  const accentGradId = `accentGrad-${svgId}`;

  const palette =
    colorScheme === 'theme-light'
      ? {
          primaryStart: '#d32f2f',
          primaryEnd: '#b71c1c',
          accent: '#ef5350',
          accentDeep: '#c62828',
          wheelOuter: '#ef5350',
          wheelInner: '#7f1212',
          detail: '#ffffff',
          textPrimary: '#9e1313',
          textSecondary: '#d32f2f',
          textMuted: '#7a4a4a',
        }
      : colorScheme === 'theme-dark'
        ? {
            primaryStart: '#ef5350',
            primaryEnd: '#c62828',
            accent: '#ff8a80',
            accentDeep: '#ef5350',
            wheelOuter: '#ff8a80',
            wheelInner: '#3b0b0b',
            detail: '#ffffff',
            textPrimary: '#ffffff',
            textSecondary: '#ffb3b3',
            textMuted: '#d6a9a9',
          }
        : {
            primaryStart: '#0E4A86',
            primaryEnd: '#1F63A8',
            accent: '#F26B38',
            accentDeep: '#D95A29',
            wheelOuter: '#D4AF37',
            wheelInner: '#1B5E20',
            detail: '#757575',
            textPrimary: '#0E4A86',
            textSecondary: '#F26B38',
            textMuted: '#757575',
          };
  const fullLogoViewBoxWidth = 500;
  const fullLogoViewBoxHeight = 150;
  const height = size;
  const width =
    variant === 'icon'
      ? size
      : (size * fullLogoViewBoxWidth) / fullLogoViewBoxHeight;

  if (variant === 'icon') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={className}
        role="img"
        aria-label="سطحة الخبر"
      >
        <defs>
          <linearGradient id={logoGradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.primaryStart} />
            <stop offset="100%" stopColor={palette.primaryEnd} />
          </linearGradient>
          <linearGradient id={accentGradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.accent} />
            <stop offset="100%" stopColor={palette.accentDeep} />
          </linearGradient>
        </defs>

        <rect x="4" y="4" width="92" height="92" rx="24" fill={`url(#${logoGradId})`} />
        <rect x="10" y="10" width="80" height="80" rx="20" fill="none" stroke={palette.accent} strokeWidth="2" opacity="0.9" />

        <g transform="translate(16, 24)">
          <rect x="6" y="28" width="44" height="7" rx="2" fill={`url(#${accentGradId})`} />
          <rect x="50" y="22" width="14" height="13" rx="3" fill={`url(#${accentGradId})`} />
          <rect x="54" y="24" width="7" height="6" rx="1.5" fill={palette.wheelInner} opacity="0.45" />
          <line x1="36" y1="28" x2="28" y2="15" stroke={palette.accent} strokeWidth="2.6" strokeLinecap="round" />
          <line x1="28" y1="15" x2="20" y2="22" stroke={palette.accent} strokeWidth="2.6" strokeLinecap="round" />
          <circle cx="28" cy="15" r="2.5" fill={palette.accent} />

          <circle cx="18" cy="40" r="7" fill={palette.wheelOuter} />
          <circle cx="18" cy="40" r="3.6" fill={palette.wheelInner} />
          <circle cx="18" cy="40" r="1.6" fill={palette.wheelOuter} />

          <circle cx="52" cy="40" r="7" fill={palette.wheelOuter} />
          <circle cx="52" cy="40" r="3.6" fill={palette.wheelInner} />
          <circle cx="52" cy="40" r="1.6" fill={palette.wheelOuter} />
        </g>
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 500 150"
      preserveAspectRatio="xMidYMid meet"
      width={width}
      height={height}
      className={className}
      role="img"
      aria-label="ابرار سطحة الخبر - Satah Al Khobar"
      style={{ direction: 'ltr' }}
      direction="ltr"
    >
      <defs>
        <linearGradient id={logoGradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.primaryStart} />
          <stop offset="100%" stopColor={palette.primaryEnd} />
        </linearGradient>
        <linearGradient id={accentGradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={palette.accent} />
          <stop offset="100%" stopColor={palette.accentDeep} />
        </linearGradient>
      </defs>

      {/* Icon circle */}
      <circle cx="72" cy="75" r="58" fill={`url(#${logoGradId})`} />
      <circle cx="72" cy="75" r="53" fill="none" stroke={palette.accent} strokeWidth="2.4" />

      {/* Truck icon in circle */}
      <g transform="translate(34, 45)" fill={palette.accent}>
        <rect x="42" y="10" width="18" height="18" rx="3" />
        <rect x="50" y="13" width="8" height="8" rx="1.5" fill={palette.wheelInner} opacity="0.5" />
        <rect x="4" y="16" width="40" height="6" rx="2" />
        <rect x="2" y="22" width="62" height="5" rx="2" />
        <line x1="32" y1="16" x2="24" y2="5" stroke={palette.accent} strokeWidth="2.5" strokeLinecap="round" />
        <line x1="24" y1="5" x2="16" y2="14" stroke={palette.accent} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="24" cy="5" r="2.5" fill={palette.accent} />
        <circle cx="14" cy="31" r="6" fill={palette.wheelOuter} />
        <circle cx="14" cy="31" r="3.5" fill={palette.wheelInner} />
        <circle cx="14" cy="31" r="1.5" fill={palette.wheelOuter} />
        <circle cx="52" cy="31" r="6" fill={palette.wheelOuter} />
        <circle cx="52" cy="31" r="3.5" fill={palette.wheelInner} />
        <circle cx="52" cy="31" r="1.5" fill={palette.wheelOuter} />
      </g>

      {/* Arabic text - primary */}
      <text
        x="150"
        y="54"
        fontFamily="'Noto Kufi Arabic', 'Tajawal', 'Cairo', Arial, sans-serif"
        fontWeight="800"
        fontSize="27"
        fill={palette.textPrimary}
        dominantBaseline="middle"
        textAnchor="start"
        direction="ltr"
      >
        ابرار سطحة الخبر
      </text>

      {/* English text - secondary */}
      <text
        x="150"
        y="86"
        fontFamily="'Poppins', 'Segoe UI', Arial, sans-serif"
        fontWeight="700"
        fontSize="15"
        fill={palette.textSecondary}
        dominantBaseline="middle"
        textAnchor="start"
        direction="ltr"
      >
        ABRAR TOWING AL-KHOBAR
      </text>

      {/* Tagline */}
      <text
        x="150"
        y="116"
        fontFamily="'Poppins', 'Segoe UI', Arial, sans-serif"
        fontWeight="500"
        fontSize="12"
        fill={palette.textMuted}
        dominantBaseline="middle"
        textAnchor="start"
        direction="ltr"
      >
        24/7 Rapid Recovery Services | خدمات سحب سريعة
      </text>

      {/* Gold accent line */}
      <line x1="150" y1="70" x2="430" y2="70" stroke={`url(#${accentGradId})`} strokeWidth="1.7" opacity="0.6" />
    </svg>
  );
}

Logo.propTypes = {
  size: PropTypes.number,
  variant: PropTypes.oneOf(['full', 'icon']),
  className: PropTypes.string,
  colorScheme: PropTypes.oneOf(['default', 'theme-light', 'theme-dark']),
};
